import React, { useState, useRef, useMemo, useEffect } from "react";
import { Canvas, extend, useFrame, useThree } from "@react-three/fiber";
import { Raycaster, Vector3, Vector2, Plane } from "three";

import { useDarkMode } from "./DarkModeSwitch";

const raycaster = new Raycaster();
const plane = new Plane(new Vector3(0, 0, 1), 0); // A plane parallel to the camera

// Utility to generate random asteroids
const generateRandomAsteroids = (count, range) => {
    return Array.from({ length: count }).map(() => ({
        position: [Math.random() * range - range / 2, Math.random() * range - range / 2, 0],
        velocity: [(Math.random() - 0.5) * 0.02, (Math.random() - 0.5) * 0.02, 0],
        shape: [0.3 + Math.random() * 0.4, Math.random() * 8, Math.random() * 4],
    }));
};

// Spaceship component
const Spaceship = ({ mouse }) => {
    const ref = useRef();
    const intersection = useRef();
    const [isDarkMode, toggleDarkMode] = useDarkMode();
    const { camera } = useThree(); // Get the camera from useThree hook

    useFrame(() => {
        if (ref.current) {
            // Map mouse to world space using raycaster
            const mouseVector = new Vector2(mouse.x, mouse.y);
            raycaster.setFromCamera(mouseVector, camera);

            intersection.current = new Vector3();
            raycaster.ray.intersectPlane(plane, intersection.current); // Get the intersection with the plane

            // Move spaceship towards the target (intersection point in world space)
            ref.current.position.lerp(intersection.current, 0.025);

            // Rotate the spaceship to face the direction of movement
            const direction = new Vector3().subVectors(intersection.current, ref.current.position).normalize();
            const angle = Math.atan2(direction.y, direction.x);
            ref.current.rotation.z = angle - Math.PI / 2;
        }
    });

    // Triangle (spaceship)
    return (
        <group>
            {/* <mesh position={intersection.current}>
                <sphereGeometry args={[0.1, 8, 8]} />
                <meshBasicMaterial color={"black"} wireframe />
            </mesh> */}
            <mesh ref={ref}>
                <coneGeometry args={[0.5, 1, 3]} />
                <meshBasicMaterial color={isDarkMode ? "white" : "black"} wireframe />
            </mesh>
        </group>
    );
};

// Asteroids component
const Asteroids = ({ count, range }) => {
    const asteroids = useMemo(() => generateRandomAsteroids(count, range), [count, range]);
    const asteroidRefs = useRef([]);
    const [isDarkMode, toggleDarkMode] = useDarkMode();
    // UseFrame to update asteroid positions
    useFrame(() => {
        asteroidRefs.current.forEach((asteroid, i) => {
            if (asteroid) {
                const { velocity } = asteroids[i];
                asteroid.position.x += velocity[0];
                asteroid.position.y += velocity[1];
                // asteroid.position.z += velocity[2];
            }
        });
    });

    return (
        <group>
            {asteroids.map((asteroid, i) => (
                <mesh key={i} ref={(el) => (asteroidRefs.current[i] = el)} position={asteroid.position}>
                    <sphereGeometry args={asteroid.shape} />
                    <meshBasicMaterial color={isDarkMode ? "white" : "black"} wireframe />
                </mesh>
            ))}
        </group>
    );
};

// Bullets component
const Bullets = ({ bullets }) => {
    const [isDarkMode, toggleDarkMode] = useDarkMode();
    return (
        <group>
            {bullets.map((bullet, idx) => (
                <mesh key={idx} position={bullet.position}>
                    <sphereGeometry args={[0.1, 8, 8]} />
                    <meshBasicMaterial color={isDarkMode ? "white" : "black"} wireframe />
                </mesh>
            ))}
        </group>
    );
};

// Main Game Component
const AsteroidsGame = ({ style = {} }) => {
    const [mouse, setMouse] = useState({ x: 0, y: 0 });
    // const camera = { position: [0, 0, 50], fov: 50 };
    // Update mouse position
    // const handleMouseMove = (e) => {
    //     setMouse({
    //         x: (e.clientX / window.innerWidth) * 2 - 1,
    //         y: -(e.clientY / window.innerHeight) * 2 + 1,
    //     });
    // };

    useEffect(() => {
        const handleMouseMove = (e) => {
            setMouse({
                x: (e.clientX / window.innerWidth) * 2 - 1,
                y: -(e.clientY / window.innerHeight) * 2 + 1,
            });
        };
        window.addEventListener("mousemove", handleMouseMove);

        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    return (
        <>
            <Canvas camera={{ position: [0, 0, 50], fov: 50 }} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100vw", height: "100vh", pointerEvents: "none", ...style }}>
                <ambientLight />
                <Spaceship mouse={mouse} />
                <Asteroids count={20} range={50} />
            </Canvas>
        </>
    );
};

export default AsteroidsGame;
